import useRole from "@/auth/hooks/useRole";
import FallbackComponent from "@/components/FallbackComponent";
import Loading from "@/components/Loading";
import { useGetValveReport } from "@/reports/api";
import ReportForm from "@/reports/components/ReportForm";
import { FORM_ACTION } from "@/reports/config/formActionType";
import useSaveReport from "@/reports/hooks/useSaveReport";
import { ReportType } from "@/reports/types/Api";
import { locationSchema, ReportId } from "@/reports/types/SafetyValveReport";
import { getPageTitle } from "@/reports/utils/report";
import { useCallback, useEffect, useState } from "react";
import { withErrorBoundary } from "react-error-boundary";
import { useNavigate, useParams } from "react-router-dom";

interface Props {
  reportType?: ReportType;
}

const Edit = ({ reportType = "SAFETY_VALVE" }: Props) => {
  const navigate = useNavigate();
  const { isViewer } = useRole();
  const { location: locationParam, id: idParam } = useParams();
  const location = locationSchema.parse(locationParam);
  const [id, setId] = useState<ReportId>(() => {
    return {
      value: String(idParam),
      onServer: true,
    };
  });

  useEffect(() => {
    if (!idParam) {
      navigate(
        reportType === "SAFETY_VALVE"
          ? "/safety-valve-reports"
          : "/manual-valve-reports"
      );
    }
  }, [idParam, navigate, reportType]);

  const { data: defaultOnlineValues, isLoading } = useGetValveReport(
    id,
    location,
    isViewer,
    reportType
  );

  const title = getPageTitle(defaultOnlineValues?.status);

  const onCancel = useCallback(() => {
    if (reportType === "MANUAL_VALVE") {
      navigate("/manual-valve-reports");
    } else {
      navigate("/safety-valve-reports");
    }
  }, [navigate, reportType]);

  const {
    save: onlineSave,
    isSaved: isSavedOnline,
    setSaved: setSavedOnline,
  } = useSaveReport({
    id,
    setId,
    location,
    reportType,
  });

  if (isLoading) return <Loading />;

  return (
    <ReportForm
      onCancel={onCancel}
      save={onlineSave}
      defaultValues={defaultOnlineValues}
      title={title}
      isSaved={isSavedOnline}
      setSaved={setSavedOnline}
      formActionType={FORM_ACTION.EDIT}
      reportType={reportType}
    />
  );
};

export default withErrorBoundary(Edit, {
  FallbackComponent,
});
